import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, Users, Loader2, CalendarDays, Check } from "lucide-react";

const empty = { date: "", start_time: "", end_time: "", title: "", description: "" };

export default function Activities() {
  const { user } = useAuth();
  const isPlanner = user?.role === "admin" || user?.role === "planner";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/activities"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ date: a.date, start_time: a.start_time || "", end_time: a.end_time || "", title: a.title, description: a.description || "" }); setEditingId(a.id); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editingId) { await api.put(`/activities/${editingId}`, form); toast.success("Activiteit bijgewerkt"); }
      else { await api.post("/activities", form); toast.success("Activiteit toegevoegd"); }
      setOpen(false); setEditingId(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Deze activiteit verwijderen?")) return;
    try { await api.delete(`/activities/${id}`); toast.success("Activiteit verwijderd"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSignup = async (a) => {
    setTogglingId(a.id);
    try {
      const { data } = a.i_signed_up ? await api.delete(`/activities/${a.id}/signup`) : await api.post(`/activities/${a.id}/signup`);
      setItems((list) => list.map((x) => (x.id === a.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTogglingId(null); }
  };

  const fmtDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Activiteiten</h1>
          <p className="text-muted-foreground mt-1">Vergaderingen en activiteiten waarvoor je je kunt aanmelden — dit zijn geen diensten.</p>
        </div>
        {isPlanner && <Button onClick={openNew} data-testid="new-activity-btn"><Plus className="w-4 h-4 mr-1.5" /> Nieuwe activiteit</Button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        : items.length === 0 ? <Card className="p-6 text-sm text-muted-foreground" data-testid="activities-empty">Er staan geen activiteiten gepland.</Card>
        : <div className="grid gap-4 md:grid-cols-2">
            {items.map((a) => (
              <Card key={a.id} className="p-4 space-y-3" data-testid={`activity-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs text-muted-foreground capitalize flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(a.date)}</p>
                    <h3 className="font-heading font-bold text-lg">{a.title}</h3>
                    {(a.start_time || a.end_time) && <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Clock className="w-4 h-4" />{a.start_time || ""}{a.end_time ? `–${a.end_time}` : ""}</p>}
                  </div>
                  {isPlanner && <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`edit-activity-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-rose-600" onClick={() => remove(a.id)} data-testid={`delete-activity-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>}
                </div>
                {a.description && <p className="text-sm whitespace-pre-line">{a.description}</p>}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" />{a.signup_count} aangemeld:</span>
                  {a.signups.length === 0 && <span className="text-xs text-muted-foreground">nog niemand</span>}
                  {a.signups.map((s) => <span key={s.user_id} className="bg-slate-100 dark:bg-slate-800 text-xs px-2 py-0.5 rounded-full">{s.user_name}</span>)}
                </div>
                <Button variant={a.i_signed_up ? "outline" : "default"} className="w-full" disabled={togglingId === a.id} onClick={() => toggleSignup(a)} data-testid={`signup-toggle-${a.id}`}>
                  {togglingId === a.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : a.i_signed_up ? <Check className="w-4 h-4 mr-2" /> : null}
                  {a.i_signed_up ? "Aangemeld — afmelden" : "Aanmelden"}
                </Button>
              </Card>
            ))}
          </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="space-y-3">
          <DialogHeader><DialogTitle>{editingId ? "Activiteit bewerken" : "Nieuwe activiteit"}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Titel</Label><Input value={form.title} onChange={set("title")} placeholder="Bijv. Teamvergadering" data-testid="activity-title" /></div>
          <div className="space-y-1.5"><Label>Datum</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="activity-date" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Begintijd (optioneel)</Label><Input type="time" value={form.start_time} onChange={set("start_time")} data-testid="activity-start" /></div>
            <div className="space-y-1.5"><Label>Eindtijd (optioneel)</Label><Input type="time" value={form.end_time} onChange={set("end_time")} data-testid="activity-end" /></div>
          </div>
          <div className="space-y-1.5"><Label>Omschrijving</Label><Textarea value={form.description} onChange={set("description")} rows={3} placeholder="Waar gaat de activiteit over?" data-testid="activity-description" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }}>Annuleren</Button>
            <Button onClick={save} disabled={busy || !form.title || !form.date} data-testid="activity-save">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingId ? "Opslaan" : "Toevoegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
